import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { v4 as uuid } from 'uuid';
import moment from 'moment';

import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import WarningIcon from '@material-ui/icons/Warning';

import Modal from '../../shared/modal';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import DatePicker from '../../shared/date-picker/DatePicker';

import { useAlert } from '../../../hooks';
import { evaluateAllocationScan, GS1_ALLOCATION_POLICY } from '../../../utils/gs1Allocation';
import { createScannerListener } from '../../../utils/scannerInput';

import './batch-control.scss';

const DATE_STATES = {
  ok: { icon: '✓', className: 'ok' },
  short: { icon: '!', className: 'warn' },
  expired: { icon: '✕', className: 'bad' },
  none: { icon: '–', className: 'none' },
};

const SCAN_WARNING_ICONS = {
  warning: WarningIcon,
  error: ErrorOutlineIcon,
  critical: ReportProblemOutlinedIcon,
};

export const GS1_REPLENISHMENT_POLICY = Object.freeze({
  ...GS1_ALLOCATION_POLICY,
  preferSerial: false,
});

const BatchControlModal = (props) => {
  const {
    open,
    onClose,
    onSubmit,
    loading,
    item,
    items = [],
    scannerEnabled = false,
    batchItems = [],
  } = props;

  const [batchState, setBatchState] = useState([]);
  const [scanStates, setScanStates] = useState([]);
  const [scanWarning, setScanWarning] = useState(null);
  const [flash, setFlash] = useState(null);

  const codeInputRefs = useRef([]);
  const batchStateRef = useRef(batchState);
  const handleScanRef = useRef(() => {});
  const flashTimerRef = useRef(null);

  const { showAlert } = useAlert();

  useEffect(() => {
    batchStateRef.current = batchState;
  }, [batchState]);

  useEffect(() => {
    if (open && item?.quantity) {
      const values = [];
      for (let index = 0; index < item.quantity; index++) {
        values[index] = batchItems[index] ? {
          id: batchItems[index]?.id,
          code: batchItems[index]?.code,
          expDate: batchItems[index]?.expDate ? moment(batchItems[index]?.expDate) : null,
        } : {
          id: uuid(),
          code: '',
          expDate: null,
        };
      }
      batchStateRef.current = values;
      setBatchState(values);
      setScanStates(Array(values.length).fill(null));
      setScanWarning(null);
      setFlash(null);
      codeInputRefs.current = codeInputRefs.current.slice(0, values.length);
    }
  }, [open, item]);

  useLayoutEffect(() => {
    if (open && scannerEnabled && batchState.length) {
      requestAnimationFrame(() => {
        const index = batchState.findIndex((batchItem) => !String(batchItem.code || '').trim());
        codeInputRefs.current[index === -1 ? 0 : index]?.focus();
      });
    }
  }, [open, scannerEnabled, batchState.length]);

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const focusNextEmpty = useCallback((values = batchStateRef.current) => {
    const index = values.findIndex((batchItem) => !String(batchItem.code || '').trim());
    if (index === -1) return;
    requestAnimationFrame(() => codeInputRefs.current[index]?.focus());
  }, []);

  const dismissWarning = useCallback(() => {
    setScanWarning(null);
    focusNextEmpty();
  }, [focusNextEmpty]);

  useEffect(() => {
    if (!scanWarning) return undefined;

    const onKeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        dismissWarning();
      }
    };

    document.addEventListener('keydown', onKeydown, true);
    return () => document.removeEventListener('keydown', onKeydown, true);
  }, [scanWarning, dismissWarning]);

  const showRowFlash = (index, type) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash({ index, type });
    flashTimerRef.current = setTimeout(() => setFlash(null), 1200);
  };

  const getGtinDetail = (scanResult) => {
    const { decoded, gtinMatches } = scanResult;
    const itemName = item.checklistDescription || item.description || item.code || 'this item';

    if (gtinMatches) return null;
    if (!item.gtin) {
      return `No GTIN is stored for ${item.code || itemName}, so the scanned product cannot be verified.`;
    }
    if (!decoded.gtin) {
      return `The barcode contains no GTIN, so it cannot be verified against ${item.code || itemName}.`;
    }
    if (decoded.gtinValid === false) {
      return `The scanned GTIN ${decoded.gtin} has an invalid check digit and may be damaged.`;
    }

    const otherItem = items.find((candidate) => candidate.gtin === decoded.gtin);
    if (otherItem) {
      const otherName = otherItem.checklistDescription || otherItem.description || otherItem.code;
      return `The scanned item is ${otherItem.code} — ${otherName}. This replenishment entry is for ${item.code} — ${itemName}.`;
    }

    return `The scanned GTIN ${decoded.gtin} does not match ${item.code || itemName}.`;
  };

  const createRowState = (scanResult) => {
    const { decoded, expiry, gtinMatches, blocked } = scanResult;
    const expiryDisplay = decoded.expiry?.display;
    const dateTitles = {
      ok: `In date — expires ${expiryDisplay} (${expiry.daysLeft} days)`,
      short: `Short dated — expires ${expiryDisplay} (${expiry.daysLeft} days)`,
      expired: `Expired ${expiryDisplay}`,
      none: 'No expiry date in barcode',
    };

    return {
      date: expiry.state,
      dateTitle: dateTitles[expiry.state],
      gtin: gtinMatches ? 'ok' : 'bad',
      gtinTitle: gtinMatches ? 'GTIN verified for this product' : getGtinDetail(scanResult),
      critical: expiry.state === 'expired',
      blocked,
    };
  };

  handleScanRef.current = (raw) => {
    const scanResult = evaluateAllocationScan(
      raw,
      item.gtin,
      new Date(),
      GS1_REPLENISHMENT_POLICY,
    );
    const {
      decoded,
      expiry,
      gtinMatches,
      unreadable,
    } = scanResult;
    const batchCode = decoded.lot;
    const expiryDisplay = decoded.expiry?.display;
    const gtinDetail = getGtinDetail(scanResult);

    if (unreadable) {
      showAlert('warning', "Couldn't read barcode. Try scanning again, or type the batch code manually.");
      return;
    }

    if (!batchCode) {
      if (expiry.state === 'expired') {
        setScanWarning({
          variant: 'critical',
          title: 'Expired product',
          message: `This item expired on ${expiryDisplay}. Nothing was added because the barcode contains no batch/LOT code.`,
        });
      } else if (!gtinMatches && decoded.gtin) {
        setScanWarning({
          variant: 'error',
          title: 'Wrong product scanned',
          message: `${gtinDetail} Nothing was added because the barcode contains no batch/LOT code.`,
        });
      } else if (gtinMatches) {
        showAlert(
          expiry.state === 'short' ? 'warning' : 'success',
          expiry.state === 'short'
            ? `Product verified, but no batch/LOT code was found. Short dated: expires ${expiryDisplay}.`
            : 'Product verified, but the barcode contains no batch/LOT code. Enter it manually.',
        );
      } else {
        showAlert('warning', 'Barcode read, but it contains no product or batch/LOT information.');
      }
      return;
    }

    const values = batchStateRef.current;
    const targetIndex = values.findIndex((batchItem) => !String(batchItem.code || '').trim());
    if (targetIndex === -1) {
      showAlert('warning', `All ${values.length} batch lines are filled. Clear one before scanning again.`);
      return;
    }

    const nextValues = values.map((batchItem, index) => (
      index === targetIndex ? {
        ...batchItem,
        code: batchCode,
        expDate: decoded.expiry ? moment(decoded.expiry.iso, 'YYYY-MM-DD') : null,
      } : batchItem
    ));
    batchStateRef.current = nextValues;
    setBatchState(nextValues);
    setScanStates((current) => {
      const nextStates = Array(nextValues.length).fill(null);
      current.forEach((state, index) => {
        if (index < nextStates.length) nextStates[index] = state;
      });
      nextStates[targetIndex] = createRowState(scanResult);
      return nextStates;
    });

    const successful = gtinMatches && expiry.state !== 'expired';
    showRowFlash(targetIndex, successful ? 'ok' : 'bad');

    if (expiry.state === 'expired') {
      const daysAgo = Math.abs(expiry.daysLeft);
      setScanWarning({
        variant: 'critical',
        title: 'Expired product',
        message: `Expired ${expiryDisplay} (${daysAgo} day${daysAgo === 1 ? '' : 's'} ago). Batch ${batchCode} must not be replenished.${gtinDetail ? ` It is also the wrong product: ${gtinDetail}` : ''}`,
      });
    } else if (gtinDetail) {
      setScanWarning({
        variant: 'error',
        title: 'Wrong product scanned',
        message: `${gtinDetail}${expiry.state === 'short' ? ` It is also short dated and expires ${expiryDisplay} (${expiry.daysLeft} days).` : ''}`,
      });
    } else if (expiry.state === 'short') {
      setScanWarning({
        variant: 'warning',
        title: 'Short dated product',
        message: `This item expires on ${expiryDisplay}, in ${expiry.daysLeft} day${expiry.daysLeft === 1 ? '' : 's'}.`,
      });
    } else {
      focusNextEmpty(nextValues);
    }
  };

  useEffect(() => {
    if (!open || !scannerEnabled) return undefined;

    const scanner = createScannerListener(
      (raw) => handleScanRef.current(raw),
      {
        onLeakCleanup: (cleanedInputs) => {
          setBatchState((current) => {
            const next = current.map((batchItem) => ({ ...batchItem }));
            cleanedInputs.forEach(({ target, value }) => {
              const index = codeInputRefs.current.indexOf(target);
              if (index !== -1) next[index].code = value;
            });
            batchStateRef.current = next;
            return next;
          });
        },
      },
    );

    return () => scanner.destroy();
  }, [open, scannerEnabled]);

  const clearScanState = (index) => {
    setScanStates((current) => {
      const states = [...current];
      states[index] = null;
      return states;
    });
  };

  const onCodeChange = (event, batchItem, index) => {
    const next = batchState.map((entry) => (
      entry.id === batchItem.id ? { ...entry, code: event.target.value } : entry
    ));
    batchStateRef.current = next;
    setBatchState(next);
    clearScanState(index);
  };

  const onClearClick = (batchItem, index) => {
    const next = batchState.map((entry) => (
      entry.id === batchItem.id ? { ...entry, code: '' } : entry
    ));
    batchStateRef.current = next;
    setBatchState(next);
    clearScanState(index);
    requestAnimationFrame(() => codeInputRefs.current[index]?.focus());
  };

  const onDateChange = (date, batchItem, index) => {
    const next = batchState.map((entry) => (
      entry.id === batchItem.id ? { ...entry, expDate: date } : entry
    ));
    batchStateRef.current = next;
    setBatchState(next);
    clearScanState(index);
  };

  const handleClose = () => {
    setScanWarning(null);
    onClose();
  };

  const hasBlockingScan = scanStates.some((state) => state?.blocked);
  const scanWarningVariant = scanWarning?.variant || 'warning';
  const ScanWarningIcon = SCAN_WARNING_ICONS[scanWarningVariant] || WarningIcon;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title='Edit Batch'
        fullWidth
        submitButton='Submit'
        closeButton='Cancel'
        onSubmit={() => onSubmit(batchState)}
        loading={loading}
        disabled={loading || hasBlockingScan}
      >
        <div className='quarantine-modal__container batch-control-modal'>
          <div className='secondary'>Edit batch information for the following item:</div>
          <div className='font-bold m-t-md'>{`${item.code} (x${item.quantity})`}</div>
          <div className='m-t-sm'>{item.checklistDescription || item.description}</div>
          {scannerEnabled && (
            <div className='batch-control-modal__scanner-note'>
              Scan a GS1 barcode or enter the batch and expiry manually. Barcodes without an expiry date are supported.
            </div>
          )}
          <div className='m-t-lg'>
            {scannerEnabled ? (
              <div className='batch-control-modal__validation-header'>
                <div>BATCH CODE</div>
                <div>EXPIRY DATE</div>
                <span>DATE</span>
                <span>GTIN</span>
              </div>
            ) : (
              <div className='d-flex m-b-md'>
                <div className='flex-1 secondary font-bold m-r-md'>BATCH CODE</div>
                <div className='flex-1 secondary font-bold m-l-md'>EXPIRY DATE</div>
              </div>
            )}
            {batchState.map((batchItem, index) => {
              const state = scanStates[index];
              const dateState = state ? DATE_STATES[state.date] : null;
              const flashClass = flash?.index === index
                ? `batch-control-modal__row--flash-${flash.type}`
                : '';

              return (
                <div
                  className={scannerEnabled
                    ? [
                      'batch-control-modal__row',
                      state?.critical ? 'batch-control-modal__row--critical' : '',
                      flashClass,
                    ].filter(Boolean).join(' ')
                    : 'd-flex'}
                  key={batchItem.id}
                >
                  <div className={scannerEnabled ? '' : 'flex-1 m-r-md'}>
                    <Input
                      ref={(element) => {
                        codeInputRefs.current[index] = element;
                      }}
                      type='text'
                      value={batchItem.code}
                      placeholder={scannerEnabled ? `${index + 1}. Add batch code` : undefined}
                      onChange={(event) => onCodeChange(event, batchItem, index)}
                      warning={!batchItem.code || (scannerEnabled && state?.date === 'short')}
                      error={scannerEnabled && state?.critical}
                      marginBottom={scannerEnabled ? 0 : undefined}
                      endAdornment={
                        !!batchItem.code && (
                          <IconButton
                            color='primary'
                            size='small'
                            aria-label='clear'
                            onClick={() => onClearClick(batchItem, index)}
                            style={{ marginRight: '8px' }}
                          >
                            <ClearIcon color='primary' fontSize='small' />
                          </IconButton>
                        )
                      }
                    />
                  </div>

                  <div className={scannerEnabled ? '' : 'flex-1 m-l-md m-b-lg'}>
                    <DatePicker
                      date={batchItem.expDate}
                      onDateChange={(date) => onDateChange(date, batchItem, index)}
                      placeholder='YYYY/MM/DD'
                      format='yyyy/MM/DD'
                      keyboard
                    />
                  </div>

                  {scannerEnabled && (
                    <>
                      <span
                        className={`batch-control-modal__validation batch-control-modal__validation--${dateState?.className || 'empty'}`}
                        title={state?.dateTitle || 'Not scanned'}
                        aria-label={`Expiry: ${state?.dateTitle || 'not validated'}`}
                      >
                        {dateState?.icon || ''}
                      </span>

                      <span
                        className={`batch-control-modal__validation batch-control-modal__validation--${state?.gtin || 'empty'}`}
                        title={state?.gtinTitle || 'Not scanned'}
                        aria-label={`GTIN: ${state?.gtinTitle || 'not validated'}`}
                      >
                        {state ? (state.gtin === 'ok' ? '✓' : '✕') : ''}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      <Modal
        open={!!scanWarning}
        onClose={dismissWarning}
        fullWidth
        size='xs'
        showCloseButton={false}
        className={`case-scan-warning-dialog case-scan-warning-dialog--${scanWarningVariant}`}
      >
        <div className='case-scan-warning' role='alert'>
          <div className='case-scan-warning__title'>
            <span className='case-scan-warning__icon' aria-hidden='true'>
              <ScanWarningIcon />
            </span>
            <span>{scanWarning?.title}</span>
          </div>
          <div className='case-scan-warning__message'>{scanWarning?.message}</div>
          <Button
            type='submit'
            text='OK'
            onClick={dismissWarning}
          />
          <div className='case-scan-warning__hint'>Press Enter to continue scanning</div>
        </div>
      </Modal>
    </>
  );
};

export default BatchControlModal;
